/**
 * FASE 22.3 — WORKFLOW PRIORITIES
 *
 * Deterministic scoring engine. No ML.
 * Produces a 0-100 score and PRIORITY level per workflow.
 */

import { getActiveWorkflows, getWorkflowById } from "./workflowState";
import { getWorkflowDefinition, PRIORITY, STEP_STATUS, WORKFLOW_STATUS } from "./workflowSchemas";

const PRIORITY_SCORE_MAP = {
  [PRIORITY.CRITICAL]: 4,
  [PRIORITY.HIGH]:     3,
  [PRIORITY.MEDIUM]:   2,
  [PRIORITY.LOW]:      1,
};

const SCORE_TO_PRIORITY = [
  { min: 75, level: PRIORITY.CRITICAL },
  { min: 50, level: PRIORITY.HIGH },
  { min: 25, level: PRIORITY.MEDIUM },
  { min: 0,  level: PRIORITY.LOW },
];

function daysSince(ms) {
  return (Date.now() - ms) / (24 * 60 * 60 * 1000);
}

/**
 * Score a single workflow.
 * @returns {{ score: number, priority: string, reasons: string[] }}
 */
export function scoreWorkflow(wf) {
  if (!wf) return { score: 0, priority: PRIORITY.LOW, reasons: [] };

  const def     = getWorkflowDefinition(wf.type);
  const reasons = [];
  let   score   = 0;

  // 1. Base: declared priority (0-40 pts)
  const basePts = (PRIORITY_SCORE_MAP[wf.priority] ?? 1) * 10;
  score += basePts;

  // 2. Inactivity relative to maxInactiveDays (0-30 pts)
  if (def?.maxInactiveDays) {
    const inactivityDays = daysSince(wf.last_activity);
    const ratio          = inactivityDays / def.maxInactiveDays;
    if (ratio >= 1) {
      score += 30;
      reasons.push(`Sin actividad por ${Math.round(inactivityDays)} días (límite: ${def.maxInactiveDays})`);
    } else if (ratio >= 0.7) {
      score += 20;
      reasons.push(`Actividad cercana al límite`);
    } else if (ratio >= 0.4) {
      score += 10;
    }
  }

  // 3. Stagnant progress — more than 7 days with same progress < 0.5 (0-15 pts)
  const ageDays = daysSince(wf.created_at);
  if (ageDays > 7 && wf.progress < 0.3) {
    score += 15;
    reasons.push("Progreso bajo después de más de una semana");
  } else if (ageDays > 14 && wf.progress < 0.5) {
    score += 10;
  }

  // 4. Pending steps with no advancement (0-10 pts)
  const pendingCount = (wf.steps ?? []).filter(
    (s) => s.status === STEP_STATUS.PENDING || s.status === STEP_STATUS.IN_PROGRESS,
  ).length;
  const totalCount = (wf.steps ?? []).length;
  if (totalCount > 0 && pendingCount === totalCount) {
    // No steps completed at all
    score += ageDays > 3 ? 10 : 5;
  }

  // 5. next_recommended_action pending (0-5 pts)
  if (wf.next_recommended_action) {
    score += 5;
  }

  score = Math.min(100, Math.round(score));

  const priority = SCORE_TO_PRIORITY.find((t) => score >= t.min)?.level ?? PRIORITY.LOW;

  return { score, priority, reasons };
}

/** Sort workflows by descending score. */
export function sortWorkflowsByPriority(workflows) {
  return workflows
    .map((w) => ({ wf: w, ...scoreWorkflow(w) }))
    .sort((a, b) => b.score - a.score)
    .map(({ wf, score, priority, reasons }) => ({ ...wf, _score: score, _priority: priority, _reasons: reasons }));
}

export function getWorkflowPriorityLevel(workflowId) {
  const wf = getWorkflowById(workflowId);
  return wf ? scoreWorkflow(wf).priority : PRIORITY.LOW;
}

/** Returns top N active workflows by computed priority score. */
export function getPriorityWorkflows(limit = 5) {
  return sortWorkflowsByPriority(getActiveWorkflows()).slice(0, limit);
}

/** Returns workflows above HIGH threshold (score >= 50). */
export function getUrgentWorkflows() {
  return getActiveWorkflows().filter((w) => {
    const { score } = scoreWorkflow(w);
    return score >= 50;
  });
}
